import { InfoPopper, InfoPopperProps, makeStyles, Theme } from '@archer-technologies/ui-components';
import { Plugin, Template, TemplateConnector, TemplatePlaceholder } from '@devexpress/dx-react-core';
import * as React from 'react';
import { ArcherGridFilter, ColumnType } from '../../Grid.types';
import { CheckColumnFilter, useCheckColumnFilterRefs } from './CheckColumnFilter';
import { DateColumnFilter, useDateColumnFilterRefs } from './DateColumnFilter';
import { getColumnFilterConfig, getColumnFilterOperations, getSelectedFilterOperation, isFilterValid, isTableHeadingCell } from './helpers';
import { NumberColumnFilter, useNumberColumnFilterRefs } from './NumberColumnFilter';
import { TextColumnFilter, useTextColumnFilterRefs } from './TextColumnFilter';
import { ClearFilterStateFn, ColumnFilterProps, FilterConfig, GetColumnFilterComponent } from './types';

interface TableHeaderRowFilterProps {
  messages: any;
  translate: any;
  addClearFilterStateFn(columnName: string, filterStateFn: ClearFilterStateFn): void;
  removeClearFilterStateFn(columnName: string): void;
  dateTimeLocaleCode?: string;
  theme: Theme;
}

export interface TableHeaderRowCellFilterProps {
  messages: any;
  addClearFilterStateFn(columnName: string, filterStateFn: ClearFilterStateFn): void;
  removeClearFilterStateFn(columnName: string): void;
  columnName: string;
  columnType: ColumnType;
  filter: ArcherGridFilter | null;
  filteringEnabled: boolean;
  getAvailableFilterOperations: any;
  changeColumnFilter: any;
  classNames: {
    mainContainer: string;
  };
  translate: any;
  dateTimeLocaleCode?: string;
  theme: Theme;
}

const useStyles = makeStyles(() => {
  return {
    mainContainer: {
      minWidth: '350px',
      '& [class*="MuiInputBase-root"]': {
        // Override height to make sure all input types are the same height
        height: '50px'
      }
    }
  };
});

const pluginDependencies = [{ name: 'Table' }, { name: 'FilteringState' }, { name: 'DataTypeProvider' }];

const TableHeaderRowFilterPlugin = ({
  messages,
  translate,
  addClearFilterStateFn,
  removeClearFilterStateFn,
  dateTimeLocaleCode,
  theme
}: TableHeaderRowFilterProps) => {
  const classNames = useStyles();
  return (
    <Plugin name="TableHeaderRowFilter" dependencies={pluginDependencies}>
      <Template name="tableCell" predicate={({ tableColumn, tableRow }: any) => isTableHeadingCell(tableColumn, tableRow)}>
        {(params: any) => (
          <TemplateConnector>
            {({ filters, isColumnFilteringEnabled, getAvailableFilterOperations }, { changeColumnFilter }) => {
              const columnName = params.tableColumn.column ? params.tableColumn.column.name : '';
              const columnType: ColumnType = params.tableColumn.column && params.tableColumn.column.type;
              const filter = getColumnFilterConfig(filters, columnName);
              const filterProps: TableHeaderRowCellFilterProps = {
                messages,
                translate,
                addClearFilterStateFn,
                removeClearFilterStateFn,
                columnName,
                columnType,
                filter,
                filteringEnabled: isColumnFilteringEnabled(columnName),
                getAvailableFilterOperations,
                changeColumnFilter,
                classNames,
                dateTimeLocaleCode,
                theme
              };
              return <TemplatePlaceholder params={{ ...params, filterProps }} />;
            }}
          </TemplateConnector>
        )}
      </Template>
    </Plugin>
  );
};

const TableHeaderRowCellFilter = ({
  messages,
  translate,
  addClearFilterStateFn,
  removeClearFilterStateFn,
  columnName,
  columnType,
  filter,
  filteringEnabled,
  getAvailableFilterOperations,
  changeColumnFilter,
  classNames,
  dateTimeLocaleCode,
  theme
}: TableHeaderRowCellFilterProps) => {
  const columnFilterOperations = getColumnFilterOperations(getAvailableFilterOperations, columnName);
  const selectedFilterOperation = getSelectedFilterOperation(filter, columnFilterOperations);

  const onFilter = React.useCallback((config: FilterConfig | null) => changeColumnFilter({ columnName, config }), [columnName, changeColumnFilter]);
  const [useFilterComponentRefs, FilterComponent] = getColumnFilterComponent(columnType);
  const filterComponentRefs = useFilterComponentRefs();
  const filterComponentProps: ColumnFilterProps = {
    filter,
    columnName,
    columnFilterOperations,
    selectedFilterOperation,
    onFilter,
    addClearFilterStateFn,
    removeClearFilterStateFn,
    messages,
    classNames,
    filterComponentRefs,
    translate,
    dateTimeLocaleCode
  };

  const infoPopperProps: InfoPopperProps = {
    buttonDataProp: `${columnName}-filter-button`,
    icon: isFilterValid(filter) ? 'FilterApplied' : 'FilterList',
    iconColor: isFilterValid(filter) ? theme.palette.primary.main : theme.palette.grey[800],
    fontSize: 'medium',
    popperPosition: 'bottom-end',
    contentItem: <FilterComponent {...filterComponentProps} />,
    childContentRefs: Object.values(filterComponentRefs.childComponentRefs || {}),
    overrideClickAwayRefs: Object.values(filterComponentRefs.handleClickAwayOverrideRefs || {}),
    disabled: !filteringEnabled,
    tooltip: isFilterValid(filter) ? 'Filter Applied' : messages.filter
  };

  return <InfoPopper {...infoPopperProps} />;
};

/**
 * Utilities
 */
export const getColumnFilterComponent: GetColumnFilterComponent = columnType => {
  switch (columnType) {
    case 'number':
      return [useNumberColumnFilterRefs, NumberColumnFilter];
    case 'check':
      return [useCheckColumnFilterRefs, CheckColumnFilter];
    case 'date':
      return [useDateColumnFilterRefs, DateColumnFilter];
    default:
      return [useTextColumnFilterRefs, TextColumnFilter];
  }
};

export { TableHeaderRowFilterPlugin, TableHeaderRowCellFilter };
